import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import Section from '@/components/Section'
import { usePageTitle } from '@/components/usePageTitle'
import { getCheckout, type CheckoutSession } from '@/lib/api'
import { hexContrast } from '@/lib/color'

/** The thank-you page after checkout. */
export default function Registered() {
  usePageTitle('Thank You', 'Your color is registered with ColorMark.')
  const [params] = useSearchParams()
  const sessionId = params.get('session_id') ?? ''
  const [session, setSession] = useState<CheckoutSession | null | 'loading'>('loading')

  useEffect(() => {
    let alive = true
    ;(sessionId ? getCheckout(sessionId) : Promise.resolve(null)).then((s) => {
      if (alive) setSession(s)
    })
    return () => {
      alive = false
    }
  }, [sessionId])

  if (session === 'loading') return <Section className="min-h-svh pt-40 md:pt-52" />
  if (!session || session.status !== 'paid') {
    return (
      <Section className="min-h-svh pt-40 md:pt-52">
        <div className="mx-auto max-w-[1800px]">
          <h1>Nothing to show yet.</h1>
          <p className="mt-8">
            {session ? (
              <>
                This registration has not been completed.{' '}
                <Link to={`/register/checkout?session_id=${encodeURIComponent(session.id)}`}>Return to checkout.</Link>
              </>
            ) : (
              <>
                That registration could not be found. <Link to="/register">Register a color.</Link>
              </>
            )}
          </p>
        </div>
      </Section>
    )
  }

  const slug = session.color?.slug ?? session.slug
  const fg = hexContrast(session.hex) === 'black' ? '#111111' : '#ffffff'

  return (
    <Section className="min-h-svh pt-40 md:pt-52">
      <div className="mx-auto max-w-[1800px]">
        <h1>Thank You!</h1>
        <div className="mt-10 flex flex-wrap items-center gap-x-3 px-5 py-6 text-[1.7em] font-body" style={{ backgroundColor: session.hex, color: fg }}>
          <Link to={`/color/${session.hex.slice(1)}`} className="no-underline hover:underline" style={{ color: fg }}>
            {session.hex}
          </Link>
          <span aria-hidden="true">-</span>
          <Link to={`/color/${slug}`} className="no-underline hover:underline" style={{ color: fg }}>
            {session.name}
          </Link>
        </div>
        <h4 className="mt-12 font-medium">Thank you for registering your unique color.</h4>
        <p className="mt-4 font-body">
          {session.name} is now listed in the registry under {session.plan.label.toLowerCase()}.
          {session.color?.expiration ? ` It is yours through ${session.color.expiration.replaceAll('-', '/')}.` : ' It is yours for life.'}
        </p>
        <ul className="mt-8 space-y-3 text-xl">
          <li>
            <Link to={`/color/${slug}`}>View Your Color Page</Link>
          </li>
          <li>
            <Link to={`/color/${slug}/certificate`}>Official Certificate of Registration</Link>
          </li>
          <li>
            <Link to={`/registry?type=hex&q=${encodeURIComponent(session.hex)}`}>Find it in the Registry</Link>
          </li>
        </ul>
      </div>
    </Section>
  )
}
